<template>
  <section v-if="isShowing" class="retake-ended" data-testid="retake-session-finished">
    <p class="retake-ended__eyebrow">
      {{ $t('retakeSessionFinished') }}
    </p>
    <h2 v-if="deckTitle" class="retake-ended__title" data-testid="retake-session-finished-title">
      {{ deckTitle }}
    </h2>
    <p v-if="liveScore !== null" class="retake-ended__score" data-testid="retake-session-finished-score">
      {{ $t('retakeSessionFinishedScore', { msg: liveScore }) }}
    </p>
    <p class="retake-ended__body">
      {{ $t('retakeSessionFinishedBody') }}
    </p>
    <ds-button
      variant="primary"
      size="touch"
      data-testid="retake-session-finished-retake-button"
      @click="startRetake('session_finished')"
    >
      <span slot="icon"><ds-icon name="system-arrow-counter-clockwise" size="16" decorative /></span>
      {{ $t('replayPractiseAgainButton') }}
    </ds-button>
    <ds-button
      v-if="canReview"
      variant="secondary-deck"
      size="touch"
      data-testid="retake-session-finished-review-button"
      @click="openReview"
    >
      <span slot="icon"><ds-icon name="system-presentation-chart" size="16" decorative /></span>
      {{ $t('review_slides') }}
    </ds-button>
    <ds-button
      v-if="isEnableFeedback"
      variant="secondary-deck"
      size="touch"
      data-testid="retake-session-finished-feedback-button"
      @click="openFeedback"
    >
      <span slot="icon"><ds-icon name="system-chat-text" size="16" decorative /></span>
      {{ $t('give_feedback') }}
    </ds-button>
  </section>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import Sentry, { featureTags } from '@/3rd-parties/sentry'
import util from '../../util'
import { loadAudienceDesignSystem } from '../../utils/designSystemLoader'
import RetakeMixin from './retake.mixin'
import DsButton from './RetakeDsButton'
import DsIcon from './RetakeDsIcon'
import retakeDeckTitle from './retakeDeckTitle'

export default {
  components: { DsButton, DsIcon },
  mixins: [RetakeMixin],
  computed: {
    ...mapState({
      activeTabView: (state) => state.activeTabView,
      presentation: (state) => state.presentation,
      quizResults: (state) => state.quiz?.quizResults || {},
      slides: (state) => state.slides || [],
    }),
    ...mapGetters(['isEnableFeedback', 'isQuizSlide']),
    ...mapGetters('replay', ['isRetakeSessionOver']),
    deckTitle() {
      return retakeDeckTitle(this.presentation)
    },
    liveScore() {
      const quizSlides = this.slides.filter((slide) => this.isQuizSlide(slide))
      // Only plugin quizzes store their points here; a total missing any quiz slide would undercount.
      const isEveryQuizScored = quizSlides.length > 0 && quizSlides.every((slide) => this.quizResults[slide.id])
      if (!isEveryQuizScored) return null
      return quizSlides.reduce((total, slide) => total + (Number(this.quizResults[slide.id].point) || 0), 0)
    },
    isShowing() {
      return !!this.retake && this.isRetakeSessionOver && this.activeTabView === 'slide'
    },
    canReview() {
      return !!this.presentation.isEnableAudienceReviewSlides
    },
  },
  watch: {
    isShowing: {
      immediate: true,
      handler(showing) {
        if (!showing) return
        loadAudienceDesignSystem().catch((error) => {
          Sentry.captureException(error, {
            extra: { message: 'load design system failed' },
            tags: featureTags({ featureFlag: 'AHA-49118-replay', featureName: 'retake-session-finished', taskId: 'SEPA-22' }),
          })
        })
        util.ga4Track('view_replay_session_finished', this.retakeTrackingPayload('session_finished'))
      },
    },
  },
  methods: {
    openFeedback() {
      util.ga4Track('click_replay_session_finished_feedback', this.retakeTrackingPayload('session_finished'))
      this.$router.push({ name: 'feedback', params: { presentationId: this.$route.params.id } })
    },
    openReview() {
      util.ga4Track('click_replay_session_finished_review', this.retakeTrackingPayload('session_finished'))
      this.$router.push({ name: 'lookback', params: this.$route.params, query: this.$route.query })
    },
  },
}
</script>

<style lang="scss" scoped>
// Covers the reactions the live room leaves behind; the menu toggle stays above it.
.retake-ended {
  position: fixed;
  inset: theme('spacing.16') 0 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: theme('spacing.2');
  padding: theme('spacing.4');
  background-color: theme('colors.base.0');
  color: theme('colors.ds.base');
  text-align: center;

  @screen sm {
    padding: theme('spacing.4') calc(50% - 263px);
  }
}

.retake-ended__eyebrow {
  margin: 0;
  font-size: theme('fontSize.sm');
  font-weight: 600;
}

.retake-ended__title {
  margin: 0;
  font-size: theme('fontSize.2xl');
  font-weight: 600;
}

.retake-ended__score {
  margin: 0;
  font-size: theme('fontSize.lg');
  font-weight: 600;
}

.retake-ended__body {
  margin: 0 0 theme('spacing.4');
  font-size: theme('fontSize.base');
}
</style>
